import { useEffect, useRef, useState, type ReactNode } from "react";

export interface Field {
  name: string;
  label: string;
  type?: "text" | "textarea" | "number" | "select" | "checkbox";
  value?: string | boolean;
  options?: Array<{ value: string; label: string }>;
  placeholder?: string;
  hint?: string;
}

export interface ModalSpec {
  title: string;
  message?: ReactNode;
  fields: Field[];
  submit: string;
  onSubmit: (values: Record<string, string | boolean>) => string | void; // return an error message to keep the dialog open
}

export function Modal({ spec, onClose }: { spec: ModalSpec; onClose: () => void }) {
  const [values, setValues] = useState<Record<string, string | boolean>>(() => Object.fromEntries(spec.fields.map((f) => [f.name, f.value ?? (f.type === "checkbox" ? false : "")])));
  const [error, setError] = useState<string | null>(null);
  const first = useRef<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null>(null);
  useEffect(() => { first.current?.focus(); if (first.current && "select" in first.current) first.current.select(); }, []);
  const submit = () => {
    const err = spec.onSubmit(values);
    if (typeof err === "string") setError(err); else onClose();
  };
  return (
    <div className="modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <form className="modal" role="dialog" aria-label={spec.title} onSubmit={(e) => { e.preventDefault(); submit(); }}
        onKeyDown={(e) => { if (e.key === "Escape") { e.stopPropagation(); onClose(); } if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); submit(); } }}>
        <h3>{spec.title}</h3>
        {spec.message && <div className="muted small">{spec.message}</div>}
        {spec.fields.map((f, i) => {
          const set = (v: string | boolean) => setValues((s) => ({ ...s, [f.name]: v }));
          const ref = i === 0 ? (el: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null) => { first.current = el; } : undefined;
          return (
            <label key={f.name} className={`field ${f.type === "checkbox" ? "check" : ""}`}>
              {f.type !== "checkbox" && <span>{f.label}</span>}
              {f.type === "textarea" ? <textarea name={f.name} ref={ref} rows={4} value={String(values[f.name])} placeholder={f.placeholder} onChange={(e) => set(e.target.value)} />
                : f.type === "select" ? <select name={f.name} ref={ref} value={String(values[f.name])} onChange={(e) => set(e.target.value)}>{f.options!.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select>
                : f.type === "checkbox" ? <><input type="checkbox" name={f.name} checked={Boolean(values[f.name])} onChange={(e) => set(e.target.checked)} /> <span>{f.label}</span></>
                : <input name={f.name} ref={ref} type={f.type === "number" ? "text" : "text"} inputMode={f.type === "number" ? "decimal" : undefined} value={String(values[f.name])} placeholder={f.placeholder} onChange={(e) => set(e.target.value)} />}
              {f.hint && <span className="muted small">{f.hint}</span>}
            </label>
          );
        })}
        {error && <div className="error small">{error}</div>}
        <div className="modal-actions">
          <button type="button" className="ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="primary">{spec.submit}</button>
        </div>
      </form>
    </div>
  );
}
